import React, { useEffect, useMemo, useState } from "react"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config.ts"
import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"
import { IOneVerifyBillerGroup } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { NIRVANA_THEME } from "@front-shared/config/theme.config.ts"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook.ts"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { AmplitudeHelper } from "../../../helpers/amplitude.helper.tsx"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { billerGroupsSelector } from "../../../store/selectors/billerGroups.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import {
    billerGroupsSelectSingle,
    billerGroupsSelectUser,
} from "../../../store/slices/billerGroups.slice"
import { billerGroupsGetSelected } from "../../../store/thunks/billerGroups.thunks"
import type { IScreenTab } from "../../../types/objects.types.ts"
import { BillerGroupMainComponent } from "./billerGroupMain.component"
import { BillerGroupsSidebarComponent } from "./billerGroupSidebar.component"

export const BillerGroupsComponent: React.FunctionComponent<
    IBillerGroupsComponentProps
> = props => {
    useScrollFix()

    useHasPermissions(OneVerifyPermission.manageBillerGroups)

    useIsFeatureFlagEnabled(
        [FeatureFlag.ONE_VERIFY_BILLER_GROUPS],
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const isTablet = useIsMobile(NIRVANA_THEME.mediaQueries.md)

    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const billerGroups = useAppSelector(billerGroupsSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    // We need this so in the moment when we are loading selected item from server
    // Sidebar still shows correct item selected
    const [sidebarSelectedBillerGroup, setSidebarSelectedBillerGroup] =
        useState<IOneVerifyBillerGroup>()

    const [groupCreationTrigger, setGroupCreationTrigger] = useState<number>()

    const isBillerGroupLoading = useMemo(
        () => RuntimeHelper.isBillerGroupsSingleItemLoading(),
        [runtimeState.isLoading],
    )

    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        AmplitudeHelper.logEvent(AmplitudeEvent.MANAGE_GROUPS_PAGE_VIEWED, {
            practiceId: selectedPracticeRole.practice.id,
            practiceName: selectedPracticeRole.practice.name,
        })
    }, [selectedPracticeRole?.practice.id])

    // Select group from server once it's selected in sidebar
    // This way we'll get full data that we need
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        // Deselect user
        dispatch(billerGroupsSelectUser())

        if (!sidebarSelectedBillerGroup) {
            // Deselect only if selected
            billerGroups.selectedItem && dispatch(billerGroupsSelectSingle({}))

            return
        }

        dispatch(
            billerGroupsGetSelected({
                payload: {
                    billerGroupId: sidebarSelectedBillerGroup.id,
                },
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [sidebarSelectedBillerGroup?.id])

    // We need to keep these in sync so sidebar shows correctly selected item
    // And if it was deselected in state, deselect in sidebar
    useEffect(() => {
        if (!billerGroups.selectedItem && sidebarSelectedBillerGroup) {
            setSidebarSelectedBillerGroup(undefined)
        }
    }, [billerGroups.selectedItem?.id])

    const onBillerGroupCreate = () => {
        // Deselect user
        dispatch(billerGroupsSelectUser())
        setGroupCreationTrigger(new Date().getTime())
    }

    return (
        <div className="relative pb-[24px]">
            <PageHelmetElement
                title="Groups"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <div
                style={{
                    height: !isTablet
                        ? `calc(100vh - ${GENERAL_CONFIG.headerHeight}px)`
                        : undefined,
                }}
                className="flex items-start md:block"
            >
                <BillerGroupsSidebarComponent
                    className="w-[300px] md:w-screen mr-[24px]"
                    onCreate={onBillerGroupCreate}
                    selectedBillerGroup={
                        isBillerGroupLoading
                            ? sidebarSelectedBillerGroup
                            : billerGroups.selectedItem ||
                              sidebarSelectedBillerGroup
                    }
                    onBillerGroupSelected={setSidebarSelectedBillerGroup}
                    onSidebarTabSelected={props.onSidebarTabSelected}
                    sidebarAvailableTabs={props.sidebarAvailableTabs}
                    sidebarSelectedTab={props.sidebarSelectedTab}
                />

                <BillerGroupMainComponent
                    className="flex-1"
                    creationTrigger={groupCreationTrigger}
                />
            </div>
        </div>
    )
}

interface IBillerGroupsComponentProps {
    onSidebarTabSelected?: (tab: IScreenTab) => void
    sidebarAvailableTabs?: IScreenTab[]
    sidebarSelectedTab?: IScreenTab
}
